import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../constants';
import { trackEvent, ClarityEvents } from '../utils/clarityTracking';

const STORAGE_KEY = 'minto_lead_popup_shown';
const INACTIVITY_MS = 30_000;
const DEV_MODE = false;

type Step = 'mobile' | 'otp' | 'success';

interface Props {
  /** External control — when true, popup opens regardless of inactivity timer */
  externalOpen?: boolean;
  /** Called when the popup should close (external mode) */
  onExternalClose?: () => void;
  /** Called after OTP verified successfully — receives the user's verified mobile */
  onVerified?: (mobile: string) => void;
  /** Custom success message override */
  successMessage?: string;
  /** Called when user clicks "Repair Now" on the feedback success screen */
  onRepairNow?: () => void;
}

const LeadCapturePopup: React.FC<Props> = ({ externalOpen, onExternalClose, onVerified, successMessage, onRepairNow }) => {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState<Step>('mobile');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resendIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const otpRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  // ── External open/close control ───────────────────────────────────
  useEffect(() => {
    if (externalOpen !== undefined) {
      if (externalOpen) {
        setVisible(true);
        setStep('mobile');
        setMobile('');
        setOtp(['', '', '', '', '', '']);
        setError('');
      } else {
        setVisible(false);
      }
    }
  }, [externalOpen]);

  // ── Inactivity detection (only when not externally controlled) ─────
  const schedulePopup = useCallback(() => {
    if (externalOpen !== undefined) return; // externally controlled — skip
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!DEV_MODE) {
        const shownAt = localStorage.getItem(STORAGE_KEY);
        if (shownAt && Date.now() - Number(shownAt) < 24 * 60 * 60 * 1000) return;
      }
      setVisible(true);
      trackEvent(ClarityEvents.LEAD_POPUP_SHOWN);
    }, INACTIVITY_MS);
  }, [externalOpen]);

  const resetTimer = useCallback(() => {
    if (!visible) schedulePopup();
  }, [visible, schedulePopup]);

  useEffect(() => {
    if (externalOpen !== undefined) return; // externally controlled — skip inactivity
    schedulePopup();
    const events = ['mousemove', 'touchstart', 'keydown', 'scroll', 'click'];
    events.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }));
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach((e) => window.removeEventListener(e, resetTimer));
    };
  }, [schedulePopup, resetTimer, externalOpen]);

  // ── Resend countdown ─────────────────────────────────────────────
  const startResendTimer = () => {
    setResendTimer(30);
    resendIntervalRef.current = setInterval(() => {
      setResendTimer((t) => {
        if (t <= 1) {
          clearInterval(resendIntervalRef.current!);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
  };

  useEffect(() => () => {
    if (resendIntervalRef.current) clearInterval(resendIntervalRef.current);
  }, []);

  // ── Dismiss ───────────────────────────────────────────────────────
  const dismiss = (reason: 'backdrop' | 'close_button' | 'success' = 'close_button') => {
    setVisible(false);
    if (onExternalClose) {
      onExternalClose();
    } else {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    }
    trackEvent(ClarityEvents.LEAD_POPUP_DISMISSED, { reason, step });
  };

  // ── Send OTP ──────────────────────────────────────────────────────
  const handleSendOtp = async () => {
    if (mobile.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber: mobile }),
      });
      if (!res.ok) throw new Error('Failed to send OTP');
      setStep('otp');
      startResendTimer();
      setTimeout(() => otpRefs[0].current?.focus(), 100);
      trackEvent(ClarityEvents.LEAD_OTP_REQUESTED, { mobile });
    } catch {
      setError('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── OTP input handling ────────────────────────────────────────────
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value.slice(-1);
    setOtp(updated);
    if (value && index < 5) otpRefs[index + 1].current?.focus();
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
  };

  // ── Verify OTP & create lead ──────────────────────────────────────
  const handleVerifyOtp = async () => {
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }
    setError('');
    setLoading(true);
    try {
      // 1. Verify OTP
      const verifyRes = await fetch(`${API_BASE_URL}/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber: mobile, OTP: otpString }),
      });
      if (!verifyRes.ok) throw new Error('Invalid OTP');

      trackEvent(ClarityEvents.LEAD_OTP_VERIFIED, { mobile });

      // 2. Create lead
      await fetch(`${API_BASE_URL}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: 'Customer',
          mobile,
          source: onVerified ? 'Feedback Request' : 'Website Popup',
          status: 'New',
          priority: 'Medium',
          notes: onVerified
            ? 'Customer requested feedback from a recent repair via website carousel'
            : 'Lead captured via inactivity popup on website',
          expectedValue: 0,
          actualValue: 0,
        }),
      });

      setStep('success');
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
      trackEvent(ClarityEvents.LEAD_CREATED, { mobile, source: 'Website Popup' });

      // 3. Notify parent if callback provided
      if (onVerified) {
        onVerified(mobile);
      }
    } catch (err: any) {
      trackEvent(ClarityEvents.LEAD_OTP_FAILED, { mobile });
      setError(err.message === 'Invalid OTP' ? 'Invalid OTP. Please try again.' : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4 pb-4 sm:pb-0">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => dismiss('backdrop')}
      />

      {/* Modal */}
      <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden animate-fade-in">

        {/* Close */}
        <button
          onClick={() => dismiss('close_button')}
          className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
        >
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Hero banner */}
        <div className="bg-gradient-to-br from-minto-600 to-minto-800 px-6 pt-8 pb-6 text-white text-center">
          <div className="text-4xl mb-2">{onVerified ? '💬' : '🎁'}</div>
          <h2 className="text-xl font-bold mb-1">{onVerified ? 'Ask for Feedback' : 'Get Exclusive Offers!'}</h2>
          <p className="text-minto-100 text-sm">
            {onVerified
              ? 'Verify your number to get the customer\'s contact and ask about their repair experience'
              : 'Special discounts on screen & battery repairs — just for you'}
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-5">

          {/* ── Step: Mobile ── */}
          {step === 'mobile' && (
            <>
              <p className="text-sm text-gray-600 mb-4 text-center">
                {onVerified ? 'Enter your WhatsApp number to verify' : 'Enter your WhatsApp number to receive your offer'}
              </p>

              <div className="flex items-center border border-gray-300 rounded-xl overflow-hidden mb-3 focus-within:border-minto-500 focus-within:ring-1 focus-within:ring-minto-500 transition-all">
                <span className="px-3 py-3 bg-gray-50 border-r border-gray-300 text-sm text-gray-600 font-medium">+91</span>
                <input
                  type="tel"
                  value={mobile}
                  onChange={(e) => {
                    setError('');
                    setMobile(e.target.value.replace(/\D/g, '').slice(0, 10));
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendOtp()}
                  placeholder="Enter mobile number"
                  className="flex-1 px-3 py-3 text-sm outline-none bg-white"
                  autoFocus
                />
              </div>

              {error && <p className="text-red-500 text-xs mb-3">{error}</p>}

              <button
                onClick={handleSendOtp}
                disabled={loading || mobile.length !== 10}
                className="w-full bg-minto-600 hover:bg-minto-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                      <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.556 4.116 1.526 5.845L.057 23.817a.5.5 0 00.624.624l5.972-1.469A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.9 0-3.68-.506-5.21-1.39l-.374-.22-3.546.872.887-3.45-.24-.386A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
                    </svg>
                    Get OTP on WhatsApp
                  </>
                )}
              </button>

              <p className="text-center text-xs text-gray-400 mt-3">
                We'll send a one-time password to verify your number
              </p>
            </>
          )}

          {/* ── Step: OTP ── */}
          {step === 'otp' && (
            <>
              <p className="text-sm text-gray-600 mb-1 text-center">
                OTP sent to <span className="font-semibold text-gray-800">+91 {mobile}</span>
              </p>
              <button
                onClick={() => { setStep('mobile'); setOtp(['', '', '', '', '', '']); setError(''); }}
                className="block mx-auto text-xs text-minto-600 mb-4"
              >
                Change number
              </button>

              {/* 6-box OTP input */}
              <div className="flex justify-center gap-3 mb-4">
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={otpRefs[i]}
                    type="tel"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    className="w-13 h-13 text-center text-xl font-bold border-2 border-gray-300 rounded-xl focus:border-minto-500 focus:outline-none transition-colors"
                    style={{ width: '3.25rem', height: '3.25rem' }}
                  />
                ))}
              </div>

              {error && <p className="text-red-500 text-xs mb-3 text-center">{error}</p>}

              <button
                onClick={handleVerifyOtp}
                disabled={loading || otp.join('').length !== 6}
                className="w-full bg-minto-600 hover:bg-minto-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (onVerified ? 'Verify & Get Contact' : 'Verify & Get Offers')}
              </button>

              <div className="text-center mt-3">
                {resendTimer > 0 ? (
                  <p className="text-xs text-gray-400">Resend OTP in {resendTimer}s</p>
                ) : (
                  <button
                    onClick={() => { setOtp(['', '', '', '', '', '']); handleSendOtp(); }}
                    className="text-xs text-minto-600 font-medium"
                  >
                    Resend OTP
                  </button>
                )}
              </div>
            </>
          )}

          {/* ── Step: Success ── */}
          {step === 'success' && (
            <div className="text-center py-2">
              <div className="text-5xl mb-3">🎉</div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">{onVerified ? 'Request Sent!' : 'You\'re all set!'}</h3>
              <p className="text-sm text-gray-500 mb-5">
                {successMessage ?? 'Our team will reach out on WhatsApp with the best repair offers for you.'}
              </p>
              {onRepairNow ? (
                <button
                  onClick={() => {
                    trackEvent(ClarityEvents.LEAD_POPUP_CTA_CLICK, { cta: 'repair_now' });
                    dismiss('success');
                    onRepairNow();
                  }}
                  className="w-full bg-minto-600 hover:bg-minto-700 text-white font-semibold py-3 rounded-xl transition-colors"
                >
                  🔧 Repair Now
                </button>
              ) : (
                <button
                  onClick={() => {
                    trackEvent(ClarityEvents.LEAD_POPUP_CTA_CLICK, { cta: 'start_booking' });
                    dismiss('success');
                  }}
                  className="w-full bg-minto-600 hover:bg-minto-700 text-white font-semibold py-3 rounded-xl transition-colors"
                >
                  Start Booking
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadCapturePopup;
